import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Supplier, SupplierCategory, SupplierStatus } from '../mocks/suppliers';

const LOGO_COLORS = ['#0D9488','#059669','#DC2626','#D97706','#7C3AED','#0891B2','#BE185D','#1D4ED8'];

function rowToSupplier(r: Record<string, unknown>): Supplier {
  const name = (r.name as string) || '';
  const logoColor = LOGO_COLORS[name.charCodeAt(0) % LOGO_COLORS.length];
  return {
    id: r.id as string,
    name,
    category: ((r.category as string) || 'Hotel & Resort') as SupplierCategory,
    status: (((r.status as string) || 'active').charAt(0).toUpperCase() + ((r.status as string) || 'active').slice(1)) as SupplierStatus,
    country: (r.country as string) || '',
    city: (r.city as string) || '',
    website: '',
    description: (r.notes as string) || '',
    logo: name.slice(0, 2).toUpperCase(),
    logoColor,
    rating: (r.rating as number) || 0,
    reviewCount: 0,
    paymentTerms: (r.payment_terms as string) || '',
    currency: (r.currency as string) || 'AED',
    contacts: r.contact_name ? [{
      name: (r.contact_name as string) || '',
      role: 'Contact',
      email: (r.contact_email as string) || '',
      phone: (r.contact_phone as string) || '',
    }] : [],
    rates: [],
    bookings: [],
    tags: (r.tags as string[]) || [],
    totalBookings: 0,
    totalCostValue: 0,
    totalSellValue: 0,
    lastUsed: '',
    contractExpiry: '',
    notes: (r.notes as string) || '',
    createdAt: (r.created_at as string) || '',
  };
}

export function useSuppliers() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('suppliers').select('*').order('created_at', { ascending: false });
    setSuppliers((data || []).map((r) => rowToSupplier(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const createSupplier = async (supplier: Partial<Supplier>) => {
    const { data, error } = await supabase.from('suppliers').insert({
      name: supplier.name,
      category: supplier.category,
      country: supplier.country,
      city: supplier.city,
      contact_name: supplier.contacts?.[0]?.name,
      contact_email: supplier.contacts?.[0]?.email,
      contact_phone: supplier.contacts?.[0]?.phone,
      status: (supplier.status || 'Active').toLowerCase(),
      currency: supplier.currency || 'AED',
      payment_terms: supplier.paymentTerms,
      rating: supplier.rating || 0,
      notes: supplier.notes,
      tags: supplier.tags || [],
    }).select().maybeSingle();
    if (!error && data) setSuppliers((prev) => [rowToSupplier(data as Record<string, unknown>), ...prev]);
    return { data, error };
  };

  const updateSupplier = async (id: string, updates: Partial<Supplier>) => {
    const { error } = await supabase.from('suppliers').update({
      name: updates.name,
      category: updates.category,
      country: updates.country,
      city: updates.city,
      contact_name: updates.contacts?.[0]?.name,
      contact_email: updates.contacts?.[0]?.email,
      contact_phone: updates.contacts?.[0]?.phone,
      status: updates.status?.toLowerCase(),
      rating: updates.rating,
      notes: updates.notes,
      tags: updates.tags,
    }).eq('id', id);
    if (!error) setSuppliers((prev) => prev.map((s) => s.id === id ? { ...s, ...updates } : s));
  };

  const deleteSupplier = async (id: string) => {
    const { error } = await supabase.from('suppliers').delete().eq('id', id);
    if (!error) setSuppliers((prev) => prev.filter((s) => s.id !== id));
  };

  return { suppliers, loading, refetch: fetchSuppliers, createSupplier, updateSupplier, deleteSupplier };
}
